import React, { useCallback, useEffect, useMemo } from 'react';
import { AddFieldType } from './worksheet-form-component-edit-modal';
import { createUseStyles } from 'react-jss';
import { Button, Typography } from '@packages/ui/shared';
import {
    DraggableWorksheetFormComponent,
    FormElementTypeId,
    Grid,
    WorksheetFormElementOption,
} from '@packages/models/api';
import {
    DynamicGridBuilder,
    DynamicGridRowType,
    DynamicGridChangeKey,
    DynamicGridChangeData,
} from './dynamic-grid/dynamic-grid-builder';
import { useDynamicGridValidation, GridLabelCharacterLength } from './hooks/useDynamicGridValidation';
import { formatCellInput } from '@packages/ui/worksheet/utils';
import { useValidationField } from './hooks/useValidationField';
import AddField from './add-field';
import colors from '@packages/core/styles/colors';
import uuid from 'uuid/v4';

const MAX_ROWS = 6;
const MAX_COLUMNS = 6;

const MIN_ROWS = 2;
const MIN_COLUMNS = 2;

const useStyles = createUseStyles({
    divider: {
        borderWidth: 2,
        borderColor: colors.grayTwo,
        paddingTop: '4px',
        paddingBottom: '4px',
    },
    buttonsContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: '8px',
        marginBottom: '16px',
    },
    validationContainer: {
        marginTop: '8px',
        marginBottom: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
});

function normalizeText(value: string | undefined | null): string {
    return (value ?? '').trim().toLowerCase();
}

function makeUniqueLabel(base: string, existingLabel: Set<string>): string {
    for (let n = 1; n < 100; n += 1) {
        const newLabel = `${base} ${n}`;
        if (!existingLabel.has(normalizeText(newLabel))) {
            return newLabel;
        }
    }
    return `${base} ${uuid()}`;
}

function sortGridByOrder(gridRows: DynamicGridRowType[]): DynamicGridRowType[] {
    if (!gridRows?.length) {
        return [];
    }
    const sortedGridRows = gridRows
        .slice()
        .sort((a, b) => a.rowOrder - b.rowOrder)
        .map((row, rowIndex) => ({
            ...row,
            rowOrder: rowIndex + 1,
            columns: (row.columns ?? [])
                .slice()
                .sort((a, b) => a.columnOrder - b.columnOrder)
                .map((column, columnIndex: number) => ({
                    ...column,
                    columnOrder: columnIndex + 1,
                })),
        }));
    return sortedGridRows;
}

interface FormWorksheetEditDynamicGridProps extends DraggableWorksheetFormComponent {
    onChange({
        formElementLabel,
        options,
        worksheetElementTooltip,
        formElementDescription,
        formElementPlaceholder,
        grid,
    }: {
        formElementLabel?: string;
        options?: WorksheetFormElementOption[] | undefined;
        worksheetElementTooltip?: string;
        formElementDescription?: string;
        formElementPlaceholder?: string | undefined;
        grid?: Grid[];
    }): void;
    addField: AddFieldType;
    setIsValid: React.Dispatch<React.SetStateAction<boolean>>;
}

export const FormEditDynamicGrid: React.FC<FormWorksheetEditDynamicGridProps> = ({
    formElementLabel,
    formElementTooltip,
    formElementDescription,
    formElementPlaceholder = '',
    grid,
    onChange,
    addField,
    setIsValid,
}) => {
    const classes = useStyles();
    const {
        fieldHandlers: { handleRequired },
        fieldErrorMessages,
        isValid,
    } = useValidationField();

    if (!grid?.length || !grid[0]?.columns?.length) {
        return (
            <>
                <AddField
                    addFieldValues={addField}
                    onchangeText={onChange}
                    formElementTooltip={formElementTooltip}
                    formElementDescription={formElementDescription}
                    formElementLabel={formElementLabel}
                    errorMessage={fieldErrorMessages?.questionLabel}
                    handleRequired={handleRequired}
                />
                <Typography>Table Unavailable</Typography>
            </>
        );
    }

    const gridValidation = useDynamicGridValidation(grid as DynamicGridRowType[]);

    useEffect(() => {
        setIsValid(isValid && gridValidation.isValid);
    }, [isValid, setIsValid, gridValidation.isValid]);

    const currentRowCount = grid.length;
    const currentColumnCount = grid[0].columns.length;

    const isAddRowButtonDisabled = currentRowCount >= MAX_ROWS;
    const isAddColumnButtonDisabled = currentColumnCount >= MAX_COLUMNS;

    const isDeleteRowDisabled = currentRowCount <= MIN_ROWS;
    const isDeleteColumnDisabled = currentColumnCount <= MIN_COLUMNS;

    const areAllColumnsRequired = useMemo(() => {
        return grid[0].columns.every((column) => column.isRequired);
    }, [grid]);

    const handleAddRow = useCallback(() => {
        if (currentRowCount >= MAX_ROWS) {
            return;
        }

        const existingRowLabels = new Set(grid.map((row) => normalizeText(row.rowLabel)));
        const newRowLabel = makeUniqueLabel('Row Label', existingRowLabels);

        const existingColumns = grid[0].columns;
        const newRow = {
            rowLabel: newRowLabel,
            rowOrder: currentRowCount + 1,
            subRows: [],
            columns: existingColumns.map((column, index: number) => ({
                worksheetElementGridId: uuid(),
                columnLabel: column.columnLabel,
                columnOrder: index + 1,
                defaultValue: '',
                subColumns: [],
                isRequired: areAllColumnsRequired,
            })),
        };

        onChange({
            grid: sortGridByOrder([...(grid as DynamicGridRowType[]), newRow as DynamicGridRowType]) as Grid[],
        });
    }, [areAllColumnsRequired, currentRowCount, onChange, grid]);

    const handleAddColumn = useCallback(() => {
        if (currentColumnCount >= MAX_COLUMNS) {
            return;
        }

        const existingColumnLabels = new Set(grid[0].columns.map((column) => normalizeText(column.columnLabel)));
        const newColumnLabel = makeUniqueLabel('Column Label', existingColumnLabels);

        const updatedRows = grid.map((row) => ({
            ...row,
            columns: [
                ...row.columns,
                {
                    worksheetElementGridId: uuid(),
                    columnLabel: newColumnLabel,
                    columnOrder: currentColumnCount + 1,
                    subColumns: [],
                    defaultValue: '',
                    isRequired: areAllColumnsRequired,
                },
            ],
        }));

        onChange({ grid: sortGridByOrder(updatedRows as DynamicGridRowType[]) as Grid[] });
    }, [areAllColumnsRequired, currentColumnCount, onChange, grid]);

    const handleRemoveRow = useCallback(
        (rowOrder: number) => {
            if (grid.length <= MIN_ROWS) {
                return;
            }

            const remainingRows = grid.filter((row) => row.rowOrder !== rowOrder);
            onChange({ grid: sortGridByOrder(remainingRows as DynamicGridRowType[]) as Grid[] });
        },
        [onChange, grid]
    );

    const handleRemoveColumn = useCallback(
        (columnOrder: number) => {
            if (grid[0].columns.length <= MIN_COLUMNS) {
                return;
            }

            const updatedRows = grid.map((row) => ({
                ...row,
                columns: row.columns.filter((column) => column.columnOrder !== columnOrder),
            }));

            onChange({ grid: sortGridByOrder(updatedRows as DynamicGridRowType[]) as Grid[] });
        },
        [onChange, grid]
    );

    const handleToggleAllColumnsRequired = useCallback(() => {
        const updatedGrid = grid.map((row) => ({
            ...row,
            columns: row.columns.map((column) => ({
                ...column,
                isRequired: !areAllColumnsRequired,
            })),
        }));

        onChange({ grid: updatedGrid });
    }, [areAllColumnsRequired, onChange, grid]);

    const handleColumnLabelChange = useCallback(
        (columnOrder: number, value: string) => {
            const trimmedValue = value.trim();
            if (trimmedValue.length > GridLabelCharacterLength.columnLabel) {
                return;
            }
            const updatedRows = grid.map((row) => ({
                ...row,
                columns: row.columns.map((column) =>
                    column.columnOrder === columnOrder ? { ...column, columnLabel: value } : column
                ),
            }));

            onChange({ grid: updatedRows });
        },
        [onChange, grid]
    );

    const handleRowLabelChange = useCallback(
        (rowOrder: number, value: string) => {
            const trimmedValue = value.trim();
            if (trimmedValue.length > GridLabelCharacterLength.rowLabel) {
                return;
            }
            const updatedRows = grid.map((row) => (row.rowOrder === rowOrder ? { ...row, rowLabel: value } : row));

            onChange({ grid: updatedRows });
        },
        [onChange, grid]
    );

    const handleCornerHeaderChange = useCallback(
        (text: string) => {
            const trimmedValue = text.trim();
            if (trimmedValue.length > GridLabelCharacterLength.cornerPlaceholder) {
                return;
            }
            onChange({ formElementPlaceholder: text });
        },
        [onChange]
    );

    const handleCellChange = useCallback(
        (worksheetElementGridId: string, value: string) => {
            const formattedValue = formatCellInput(FormElementTypeId.DynamicGrid, value);

            const updatedRows = grid.map((row) => ({
                ...row,
                columns: row.columns.map((column) =>
                    column.worksheetElementGridId === worksheetElementGridId
                        ? { ...column, defaultValue: formattedValue }
                        : column
                ),
            }));

            onChange({ grid: updatedRows });
        },
        [onChange, grid]
    );

    const handleGridChange = useCallback(
        (key: DynamicGridChangeKey, data: DynamicGridChangeData) => {
            switch (key) {
                case 'corner':
                    handleCornerHeaderChange(data.value);
                    break;
                case 'column-label':
                    handleColumnLabelChange(data.columnOrder!, data.value);
                    break;
                case 'row-label':
                    handleRowLabelChange(data.rowOrder!, data.value);
                    break;
                case 'cell':
                    handleCellChange(data.worksheetElementGridId!, data.value);
                    break;
            }
        },
        [handleCornerHeaderChange, handleColumnLabelChange, handleRowLabelChange, handleCellChange]
    );

    return (
        <>
            <AddField
                addFieldValues={addField}
                onchangeText={onChange}
                formElementTooltip={formElementTooltip}
                formElementDescription={formElementDescription}
                formElementLabel={formElementLabel}
                errorMessage={fieldErrorMessages?.questionLabel}
                handleRequired={handleRequired}
            />

            <hr className={classes.divider} />

            {gridValidation.errors.length > 0 && (
                <div className={classes.validationContainer}>
                    {gridValidation.errors.map((errorMessage, index) => (
                        <Typography key={`grid-validation-${index}`} color="redOne">
                            {errorMessage}
                        </Typography>
                    ))}
                </div>
            )}

            <DynamicGridBuilder
                areAllColumnsRequired={areAllColumnsRequired}
                cornerHeader={formElementPlaceholder}
                isDeleteColumnButtonDisabled={isDeleteColumnDisabled}
                isDeleteRowButtonDisabled={isDeleteRowDisabled}
                onChange={handleGridChange}
                onDeleteColumn={handleRemoveColumn}
                onDeleteRow={handleRemoveRow}
                onToggleRequired={handleToggleAllColumnsRequired}
                rows={grid as DynamicGridRowType[]}
            />

            <div className={classes.buttonsContainer}>
                <Button onPress={handleAddRow} variant="link-blue" disabled={isAddRowButtonDisabled}>
                    Add Row
                </Button>
                <Button onPress={handleAddColumn} variant="link-blue" disabled={isAddColumnButtonDisabled}>
                    Add Column
                </Button>
            </div>
        </>
    );
};
